/**
 * Full songs kept on this device, so a song already fetched isn't fetched again.
 *
 * Each copy carries the song's `updatedAt`, and is only used while the team song list still gives
 * the same one — the list is the freshness check, so there is no request to ask whether a copy is
 * current. Copies are kept in IndexedDB rather than localStorage, which full songs would outgrow.
 *
 * A copy also carries who it was fetched as: an admin's copy holds hidden parts, so it is never
 * handed to anyone else on the device, and the whole cache is cleared on sign out.
 *
 * Copies unused for a while are dropped, and if the browser refuses a write as full, the least
 * recently used go first — a dropped copy only costs a fetch.
 */

import songService from '../services/songService';
import { isQuotaError } from './storage';

const DB_NAME = 'migrupo-songs';
const STORE = 'songs';

const DAY_MS = 24 * 60 * 60 * 1000;
// A copy not used in this long is dropped, which is also how songs deleted from the team go
const MAX_AGE_MS = 60 * DAY_MS;
const MAX_SONGS = 300;
// How much of the cache to drop when the browser says it's full
const EVICT_FRACTION = 0.25;
// A copy used again is re-stamped at most this often, so reading a service isn't a write every time
const TOUCH_AFTER_MS = DAY_MS;

let dbPromise = null;

const openDb = () => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB unavailable'));

      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'id' });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    // A failed open is retried next time rather than remembered
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }

  return dbPromise;
};

const run = async (mode, work) => {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = work(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(request?.result);
    tx.onerror = () => reject(tx.error);
  });
};

const readRecords = async (ids) => {
  try {
    const records = await Promise.all(ids.map((id) => run('readonly', (store) => store.get(id))));
    return records.filter(Boolean);
  } catch {
    return [];
  }
};

const readAllRecords = async () => {
  try {
    return (await run('readonly', (store) => store.getAll())) || [];
  } catch {
    return [];
  }
};

const deleteRecords = async (ids) => {
  if (ids.length === 0) return;

  try {
    await run('readwrite', (store) => {
      ids.forEach((id) => store.delete(id));
    });
  } catch {
    // Left for the next prune
  }
};

// Copies saved before lastUsed was recorded count as the oldest
const lastUsedOf = (record) => record.lastUsed || 0;

/** The ids of the least recently used copies, `fraction` of them and at least one. */
export const pickEvictions = (records, fraction = EVICT_FRACTION) =>
  [...records]
    .sort((a, b) => lastUsedOf(a) - lastUsedOf(b))
    .slice(0, Math.max(1, Math.ceil(records.length * fraction)))
    .map((record) => record.id);

/** The ids of copies unused past MAX_AGE_MS, plus the least recently used beyond MAX_SONGS. */
export const planPrune = (records, now = Date.now()) => {
  const expired = records.filter((record) => now - lastUsedOf(record) > MAX_AGE_MS);
  const kept = records
    .filter((record) => now - lastUsedOf(record) <= MAX_AGE_MS)
    .sort((a, b) => lastUsedOf(b) - lastUsedOf(a));

  return [...expired, ...kept.slice(MAX_SONGS)].map((record) => record.id);
};

const putRecords = (records) =>
  run('readwrite', (store) => {
    records.forEach((record) => store.put(record));
  });

const writeRecords = async (records) => {
  if (records.length === 0) return;

  try {
    await putRecords(records);
  } catch (error) {
    if (!isQuotaError(error)) return;

    // Full: let go of the least recently used and try once more
    const existing = await readAllRecords();
    if (existing.length === 0) return;
    await deleteRecords(pickEvictions(existing));

    try {
      await putRecords(records);
    } catch {
      // A cache that can't be written only costs a fetch next time
    }
  }
};

const pruneCache = async () => {
  const now = Date.now();
  // Copies from before lastUsed was recorded start their clock now, rather than all going at once
  const records = (await readAllRecords()).map((record) => (record.lastUsed ? record : { ...record, lastUsed: now }));

  await writeRecords(records.filter((record) => record.lastUsed === now));
  await deleteRecords(planPrune(records, now));
};

/** Who copies are fetched as; a copy is only ever handed back to the same user, team and role. */
export const getOwnerKey = (user) => (user ? `${user._id}:${user.teamId}:${Boolean(user.isAdmin)}` : null);

/**
 * Which of the listed songs have a current copy, and which have to be fetched.
 *
 * @param {Array} listSongs - Songs off the team list, carrying `updatedAt`
 * @param {Array} records - Cached copies, `{ id, updatedAt, owner, song }`
 * @param {string} owner - From getOwnerKey
 * @returns {{ fresh: Map, stale: Map, toFetch: string[] }} - Copies by song id, and the ids to fetch
 */
export const planSongFetch = (listSongs, records, owner) => {
  const byId = new Map(records.filter((record) => record.owner === owner).map((record) => [record.id, record]));
  const fresh = new Map();
  const stale = new Map();
  const toFetch = [];

  listSongs.forEach(({ _id, updatedAt }) => {
    const record = byId.get(_id);

    // A list song with no updatedAt can't be checked, so its copy is never trusted as current
    if (record && updatedAt && record.updatedAt === updatedAt) {
      fresh.set(_id, record.song);
    } else {
      if (record) stale.set(_id, record.song);
      toFetch.push(_id);
    }
  });

  return { fresh, stale, toFetch };
};

/**
 * The listed songs in full, from the cache where it is current and in one request for the rest.
 *
 * If that request fails, out-of-date copies stand in for the songs that have one, and `fromCache`
 * says so. Songs with no copy at all are left out, so callers check they got every song they asked for.
 *
 * @param {Array} listSongs - Songs off the team list, carrying `updatedAt`
 * @param {Object} user - The signed-in user, from authService
 * @returns {Promise<{ songs: Array, fromCache: boolean }>} - Full songs, in the order asked for
 */
export const loadFullSongs = async (listSongs, user) => {
  const owner = getOwnerKey(user);
  const records = owner ? await readRecords(listSongs.map((song) => song._id)) : [];
  const { fresh, stale, toFetch } = planSongFetch(listSongs, records, owner);
  let fromCache = false;
  const now = Date.now();

  if (owner) {
    const touched = records
      .filter((record) => fresh.has(record.id) && now - lastUsedOf(record) > TOUCH_AFTER_MS)
      .map((record) => ({ ...record, lastUsed: now }));
    await writeRecords(touched);
  }

  if (toFetch.length > 0) {
    try {
      const fetched = await songService.getSongsBatch(toFetch);

      fetched.forEach((song) => fresh.set(song._id, song));
      if (owner) {
        await writeRecords(
          fetched.map((song) => ({ id: song._id, updatedAt: song.updatedAt, owner, song, lastUsed: now })),
        );
        await pruneCache();
      }
    } catch {
      stale.forEach((song, id) => fresh.set(id, song));
      fromCache = stale.size > 0;
    }
  }

  const songs = listSongs.map((song) => fresh.get(song._id)).filter(Boolean);

  return { songs, fromCache };
};

/** How many songs are cached on this device, and roughly how much room they take, in characters. */
export const getSongCacheUsage = async () => {
  const records = await readAllRecords();
  const characters = records.reduce((total, record) => total + JSON.stringify(record).length, 0);

  return { count: records.length, characters };
};

/** Empties the cache. */
export const clearSongCache = async () => {
  try {
    await run('readwrite', (store) => store.clear());
  } catch {
    // Nothing was stored, or nothing can be
  }
};
